import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Edit, Building, User, SquarePlus, RefreshCw } from 'lucide-react';
import {
  PageHeader,
  PageTitle,
  PageDescription,
  PageHeaderInfo,
  PageContent,
  PageAction,
} from '@/components/layout/page-header';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  StyledTable,
  StyledTableWrapper,
  StyledTableHeader,
  StyledTableBody,
  StyledTableRow,
  StyledTableHead,
  StyledTableCell,
} from '@/components/ui/styled-table';
// NOTE: Member/group selection is intentionally disabled on this page for now.
import { AdminUsersSelector } from '@/pages/settings/components/admin-users-selector';
import { PendingChangesAlert } from '@/components/shared/pending-changes-alert';
import { Input } from '@/components/ui/input';
import { useMyOrganization } from '@/hooks/organization/use-my-organization';
import { useListMembers } from '@/hooks/organization/use-list-members';
import { useCreateAdminChangeProposal } from '@/hooks/admin/organization/use-create-admin-change-proposal';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useActiveAdminChangeProposal } from '@/hooks/admin/organization/use-active-admin-change-proposal';
import { AdminChangeComparisonDialog } from '@/pages/settings/components/admin-change-comparison-dialog';
import { ApiKeyModal } from '@/pages/settings/components/api-key-modal';
import { DefaultExpirationModal } from '@/pages/settings/components/default-expiration-modal';
import { formatExpirationMinutes } from '@/pages/settings/components/default-expiration-utils';
import { TransactionVisibilityModal } from '@/pages/settings/components/transaction-visibility-modal';
import { DisasterRecoverySetupDialog } from '@/pages/settings/components/disaster-recovery-setup-dialog';
import {
  DRProposalReviewDialog,
  type DRProposalDialogVariant,
} from '@/pages/settings/components/disaster-recovery-proposal-review-dialog';
import { DRReviewEducationGateDialog } from '@/pages/settings/components/disaster-recovery-dialogs';
import { DisasterRecoveryLearnMoreLink } from '@/pages/settings/components/disaster-recovery-learn-more-link';
import {
  DRSettingsValue,
  DRSettingsValueSkeleton,
} from '@/pages/settings/components/disaster-recovery-settings-value';
import { useApiKeyInfo } from '@/hooks/admin/organization/use-api-key-info';
import { useAuthClient } from '@/lib/authClient';
import { formatName } from '@/lib/name';
import { useDisasterRecoverySettings } from '@/hooks/admin/organization/use-disaster-recovery-settings';
import { getOrgProposalSignatureCountsAndUserVote } from '@/utils/SignatureCountsHelpers';
import { getTransactionVisibilityLabel } from '@mls-wallet/shared-types';
import { SIGNABLE_POLL_INTERVAL_MS } from '@/components/execution/execution-polling';

interface OrganizationSettings {
  organizationName: string;
}

// TODO: Refactor this page
// https://linear.app/onchainden/issue/DEN-58/refactor-settings-page
export function SettingsPage() {
  const [isEditable, setIsEditable] = useState(false);
  const [isReviewAdminChangesOpen, setIsReviewAdminChangesOpen] =
    useState(false);
  const [isApiKeyModalOpen, setIsApiKeyModalOpen] = useState(false);
  const [isDefaultExpirationModalOpen, setIsDefaultExpirationModalOpen] =
    useState(false);
  const [
    isTransactionVisibilityModalOpen,
    setIsTransactionVisibilityModalOpen,
  ] = useState(false);
  const [isDRSetupDialogOpen, setIsDRSetupDialogOpen] = useState(false);
  const [isDRReviewGateOpen, setIsDRReviewGateOpen] = useState(false);
  const [activeDRProposalDialogVariant, setActiveDRProposalDialogVariant] =
    useState<DRProposalDialogVariant | null>(null);
  const [
    disasterRecoverySetupSuccessSignal,
    setDisasterRecoverySetupSuccessSignal,
  ] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const {
    organization,
    isLoading: isOrganizationLoading,
    refetch: refetchOrganization,
  } = useMyOrganization();
  const { members } = useListMembers(organization?.id ?? null);
  const {
    createProposal: createAdminChangeProposal,
    isLoading: isCreatingAdminChangeProposal,
  } = useCreateAdminChangeProposal();
  const { activeProposal } = useActiveAdminChangeProposal();
  const { apiKeyInfo, refresh: refreshApiKeyInfo } = useApiKeyInfo(
    organization?.id ?? null
  );
  const {
    drConfig,
    drInitiationProposal: activeDRProposal,
    drCancellationProposal,
    drFinalizationProposal,
    isLoading: isDRSettingsLoading,
  } = useDisasterRecoverySettings({
    swrOptions: { refreshInterval: SIGNABLE_POLL_INTERVAL_MS },
  });
  const { user: currentUser } = useAuthClient();
  const currentUserId = currentUser?.id ?? null;
  const hasPendingInitiationProposal =
    activeDRProposal?.proposal?.status === 'PENDING';
  const {
    hasApproved: hasCurrentUserApprovedInitiationProposal,
    hasRejected: hasCurrentUserRejectedInitiationProposal,
  } = getOrgProposalSignatureCountsAndUserVote({
    approvals: activeDRProposal?.approvals ?? [],
    rejections: activeDRProposal?.rejections ?? [],
    userId: currentUserId ?? undefined,
  });
  const hasCurrentUserSignedPendingInitiationProposal =
    hasPendingInitiationProposal &&
    (hasCurrentUserApprovedInitiationProposal ||
      hasCurrentUserRejectedInitiationProposal);
  // Keep one setup dialog instance and derive whether it should open in setup or review mode.
  const drSetupDialogMode: 'setup' | 'review' =
    hasPendingInitiationProposal &&
    !hasCurrentUserSignedPendingInitiationProposal
      ? 'review'
      : 'setup';
  const pendingAdminChangesCount = activeProposal ? 1 : 0;
  const isAdmin = organization?.isAdmin ?? false;
  const isDRSettingsReady = !isOrganizationLoading && !isDRSettingsLoading;

  const openInitiationProposalReview = () => {
    if (!isAdmin) return;
    // Prevent reopening initiation review after a rejection execution has settled the proposal.
    if (!hasPendingInitiationProposal) return;
    setActiveDRProposalDialogVariant('initiation-review');
  };

  const openDRCancelDialog = (mode: 'create' | 'review') => {
    if (!isAdmin) return;
    setActiveDRProposalDialogVariant(
      mode === 'review' ? 'cancellation-review' : 'cancellation-create'
    );
  };

  const openDRFinalizeDialog = (mode: 'create' | 'review') => {
    if (!isAdmin) return;
    setActiveDRProposalDialogVariant(
      mode === 'review' ? 'finalization-review' : 'finalization-create'
    );
  };

  const activeDRDialogProposal =
    activeDRProposalDialogVariant === 'initiation-review'
      ? activeDRProposal
      : activeDRProposalDialogVariant?.startsWith('cancellation')
        ? drCancellationProposal
        : activeDRProposalDialogVariant?.startsWith('finalization')
          ? drFinalizationProposal
          : null;

  const activeDRDialogConfig =
    activeDRProposalDialogVariant === 'initiation-review'
      ? activeDRProposal?.proposal
        ? {
            recoveryWalletAddress:
              activeDRProposal.proposal.recoveryWalletAddress,
            timelockDurationSeconds:
              activeDRProposal.proposal.timelockDurationSeconds,
          }
        : null
      : drConfig;

  // Map admin user IDs to their names for display
  const adminUsersWithNames = useMemo(() => {
    if (!organization) return [];
    const memberById = new Map(members.map((m) => [m.id, m]));
    return organization.adminUsers.map((id) => ({
      id,
      name: formatName(memberById.get(id)?.name ?? id),
    }));
  }, [organization, members]);

  // Determine current tab based on URL
  const getCurrentTab = () => {
    if (location.pathname.includes('/personal')) return 'personal';
    return 'organization'; // default
  };

  const currentTab = getCurrentTab();

  // Handle tab change
  const handleTabChange = (value: string) => {
    if (value === 'organization') {
      navigate('/settings/organization');
    } else if (value === 'personal') {
      navigate('/settings/personal');
    }
  };

  const [settings, setSettings] = useState<OrganizationSettings>({
    organizationName: organization?.name || 'Acme Corp',
  });

  // Keep organization name in sync when it loads
  useEffect(() => {
    if (organization?.name) {
      setSettings((prev) => ({ ...prev, organizationName: organization.name }));
    }
  }, [organization?.name]);

  const renderSettingValue = (
    value: string | number | boolean | undefined,
    onChange:
      | ((value: string) => void)
      | ((value: boolean) => void)
      | (() => void),
    type:
      | 'text'
      | 'text-readonly'
      | 'admin-users'
      | 'api-key'
      | 'expiration-default'
      | 'transaction-visibility'
  ) => {
    // Case: settings page is not in edit mode - render the persisted read-only value.
    if (!isEditable) {
      if (type === 'text' || type === 'text-readonly') {
        return <span className="text-sm">{value as string}</span>;
      } else if (type === 'expiration-default') {
        return (
          <span className="text-sm">
            {formatExpirationMinutes(
              typeof value === 'number' ? value : undefined
            )}
          </span>
        );
      } else if (type === 'transaction-visibility') {
        const hideTransactionsFromNonPolicyParticipants = Boolean(value);
        return (
          <Badge variant="secondary" className="text-xs font-normal">
            {getTransactionVisibilityLabel(
              hideTransactionsFromNonPolicyParticipants
            )}
          </Badge>
        );
      } else if (type === 'api-key') {
        if (!apiKeyInfo.exists) {
          return (
            <span className="text-sm text-muted-foreground">
              No API key issued
            </span>
          );
        }
        // Show key prefix and creation info
        const createdDate = apiKeyInfo.createdAt
          ? new Date(apiKeyInfo.createdAt).toLocaleDateString()
          : 'Unknown';
        // Format the creator name the same way as admin users
        const formattedCreatorName = apiKeyInfo.createdByUserName
          ? formatName(apiKeyInfo.createdByUserName)
          : null;
        return (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <code className="px-2 py-1 bg-muted rounded text-xs font-mono">
                {apiKeyInfo.prefix}
                {'•'.repeat(32)}
              </code>
            </div>
            <div className="text-xs text-muted-foreground">
              Created {createdDate}
              {formattedCreatorName && (
                <>
                  {' '}
                  by{' '}
                  <Badge variant="secondary" className="text-xs font-normal">
                    {formattedCreatorName}
                  </Badge>
                </>
              )}
            </div>
          </div>
        );
      } else if (type === 'admin-users') {
        if (!organization || organization.adminUsers.length === 0) {
          return <span className="text-sm text-muted-foreground">Not set</span>;
        }

        return (
          <div className="space-y-2">
            <span className="text-sm">
              {organization.adminUsers.length} admin
              {organization.adminUsers.length === 1 ? '' : 's'} (
              {organization.numberOfApprovalsNeeded} approval
              {organization.numberOfApprovalsNeeded === 1 ? '' : 's'} required)
            </span>
            <div className="text-xs text-muted-foreground">
              Admin changes require {organization.numberOfApprovalsNeeded} of{' '}
              {organization.adminUsers.length} admin approvals
            </div>
            <div className="flex flex-wrap gap-1.5 pt-1">
              {adminUsersWithNames.map((admin) => (
                <Badge
                  key={admin.id}
                  variant="secondary"
                  className="text-xs font-normal"
                >
                  {admin.name}
                </Badge>
              ))}
            </div>
          </div>
        );
      }
    }

    switch (type) {
      case 'text':
        return (
          <Input
            value={value as string}
            onChange={(e) =>
              (onChange as (value: string) => void)(e.target.value)
            }
            className="max-w-md"
            size="sm"
          />
        );
      case 'text-readonly':
        // Disabled by product decision for now (display-only)
        return <span className="text-sm">{value as string}</span>;
      case 'api-key':
        return (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsApiKeyModalOpen(true)}
          >
            {apiKeyInfo.exists ? (
              <RefreshCw className="h-4 w-4 mr-2" />
            ) : (
              <SquarePlus className="h-4 w-4 mr-2" />
            )}
            {apiKeyInfo.exists ? 'Rotate API Key' : 'Generate API Key'}
          </Button>
        );
      case 'expiration-default':
        return (
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm">
              {formatExpirationMinutes(
                typeof value === 'number' ? value : undefined
              )}
            </span>
            {/* Case: edit mode stays readable by showing the current value beside the edit action. */}
            <Button
              variant="outline"
              size="sm"
              onClick={onChange as () => void}
              disabled={!organization}
            >
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </Button>
          </div>
        );
      case 'transaction-visibility':
        return (
          <div className="flex flex-wrap items-center gap-3">
            <Badge variant="secondary" className="text-xs font-normal">
              {getTransactionVisibilityLabel(Boolean(value))}
            </Badge>
            <Button
              variant="outline"
              size="sm"
              onClick={onChange as () => void}
              disabled={!organization}
            >
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </Button>
          </div>
        );
      case 'admin-users':
        return (
          <AdminUsersSelector
            organizationId={organization?.id ?? null}
            orgAdminUserIds={organization?.adminUsers ?? []}
            orgApprovalsNeeded={organization?.numberOfApprovalsNeeded ?? 1}
            isLoadingOrg={!organization}
            createAdminChangeProposal={createAdminChangeProposal}
            isCreating={isCreatingAdminChangeProposal}
          />
        );
      default:
        return null;
    }
  };

  const organizationSettings = [
    {
      title: 'Organization Name',
      description: 'The name of your organization',
      value: settings.organizationName,
      onChange: (value: string) =>
        setSettings((prev) => ({ ...prev, organizationName: value })),
      type: 'text-readonly' as const,
    },
  ];

  const adminSettings = [
    {
      title: 'Who has admin privilege',
      description:
        'Admins can modify admin settings, policies, members, groups, and accounts',
      value: undefined,
      onChange: () => {},
      type: 'admin-users' as const,
      settingType: 'admin-privilege',
    },
    {
      title: 'Manage organization API key',
      description:
        'Use the key to authenticate API requests and perform actions programmatically',
      value: undefined,
      onChange: () => {},
      type: 'api-key' as const,
      settingType: 'api-key',
    },
    {
      title: 'Expiration time',
      description:
        'Lifetime of new proposals and account transactions before expiry',
      value: organization?.expirationMinutes,
      onChange: () => setIsDefaultExpirationModalOpen(true),
      type: 'expiration-default' as const,
      settingType: 'default-expiration-time',
    },
    {
      title: 'Transaction visibility',
      description:
        'Control whether non-admin members can see transactions outside their policy role',
      value: organization?.hideTransactionsFromNonPolicyParticipants ?? false,
      onChange: () => setIsTransactionVisibilityModalOpen(true),
      type: 'transaction-visibility' as const,
      settingType: 'transaction-visibility',
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader>
        <PageHeaderInfo>
          <PageTitle>Settings</PageTitle>
          <PageDescription>
            Manage your application and organization settings
          </PageDescription>
        </PageHeaderInfo>
        <PageAction>
          {isAdmin ? (
            <div className="flex flex-col items-end gap-4">
              <PendingChangesAlert
                pendingChangesCount={pendingAdminChangesCount}
                onReviewChanges={() => setIsReviewAdminChangesOpen(true)}
              />
              {/* Keep edit mode available even with pending admin proposals so other admin settings remain editable. */}
              <Button
                variant={isEditable ? 'default' : 'outline'}
                onClick={() => setIsEditable(!isEditable)}
              >
                <Edit className="h-4 w-4 mr-1" />
                {isEditable ? 'Done' : 'Edit Settings'}
              </Button>
            </div>
          ) : null}
        </PageAction>
      </PageHeader>

      <PageContent>
        <Tabs
          value={currentTab}
          onValueChange={handleTabChange}
          className="w-full"
        >
          <TabsList className="flex flex-row items-stretch mb-6 border-border border text-xs w-fit p-0">
            <TabsTrigger value="organization" className="text-xs">
              <Building className="h-4 w-4 mr-1" />
              Organization
            </TabsTrigger>
            <TabsTrigger value="personal" className="text-xs">
              <User className="h-4 w-4 mr-1" />
              Personal
            </TabsTrigger>
          </TabsList>

          <TabsContent value="organization" className="mt-0">
            <div className="flex flex-col gap-6">
              {/* Organization Settings */}
              <StyledTableWrapper>
                <StyledTable>
                  <StyledTableHeader>
                    <tr>
                      <StyledTableHead>General settings</StyledTableHead>
                      <StyledTableHead className="w-1/2">Value</StyledTableHead>
                    </tr>
                  </StyledTableHeader>
                  <StyledTableBody>
                    {organizationSettings.map((setting, index) => (
                      <StyledTableRow key={index}>
                        <StyledTableCell>
                          <div className="space-y-1">
                            <div className="font-medium text-sm">
                              {setting.title}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {setting.description}
                            </div>
                          </div>
                        </StyledTableCell>
                        <StyledTableCell>
                          {renderSettingValue(
                            setting.value,
                            setting.onChange,
                            setting.type
                          )}
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}
                  </StyledTableBody>
                </StyledTable>
              </StyledTableWrapper>

              {/* Admin Settings */}
              <StyledTableWrapper>
                <StyledTable>
                  <StyledTableHeader>
                    <tr>
                      <StyledTableHead>Admin settings</StyledTableHead>
                      <StyledTableHead className="w-1/2">Value</StyledTableHead>
                    </tr>
                  </StyledTableHeader>
                  <StyledTableBody>
                    {adminSettings.map((setting, index) => (
                      <StyledTableRow key={index}>
                        <StyledTableCell>
                          <div className="space-y-1">
                            <div className="font-medium text-sm">
                              {setting.title}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {setting.description}
                            </div>
                          </div>
                        </StyledTableCell>
                        <StyledTableCell>
                          {renderSettingValue(
                            setting.value,
                            setting.onChange,
                            setting.type
                          )}
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}
                  </StyledTableBody>
                </StyledTable>
              </StyledTableWrapper>

              {/* Disaster Recovery Settings */}
              <StyledTableWrapper>
                <StyledTable>
                  <StyledTableHeader>
                    <tr>
                      <StyledTableHead>
                        Disaster Recovery settings
                      </StyledTableHead>
                      <StyledTableHead className="w-1/2">Value</StyledTableHead>
                    </tr>
                  </StyledTableHeader>
                  <StyledTableBody>
                    <StyledTableRow>
                      <StyledTableCell>
                        <div className="space-y-1">
                          <div className="font-medium text-sm">
                            Who can execute emergency account transactions
                            during disaster recovery?
                          </div>
                          <div className="text-xs text-muted-foreground">
                            A privileged wallet address can execute emergency
                            transactions in the event that MLS is not
                            operational
                          </div>
                          <DisasterRecoveryLearnMoreLink />
                        </div>
                      </StyledTableCell>
                      <StyledTableCell>
                        {isDRSettingsReady ? (
                          <DRSettingsValue
                            canManageDisasterRecovery={isAdmin}
                            drConfig={drConfig}
                            drInitiationProposal={activeDRProposal}
                            drCancellationProposal={drCancellationProposal}
                            drFinalizationProposal={drFinalizationProposal}
                            currentUserId={currentUserId}
                            onSetup={() => {
                              if (!isAdmin) return;
                              setIsDRSetupDialogOpen(true);
                            }}
                            onReviewInitiationProposal={(userHasSigned) => {
                              if (!isAdmin) return;
                              if (userHasSigned) {
                                openInitiationProposalReview();
                              } else {
                                setIsDRReviewGateOpen(true);
                              }
                            }}
                            onOpenCancelDialog={() =>
                              openDRCancelDialog('create')
                            }
                            onOpenFinalizeDialog={() =>
                              openDRFinalizeDialog('create')
                            }
                            onOpenCancellationProposalReview={() =>
                              openDRCancelDialog('review')
                            }
                            onOpenFinalizationProposalReview={() =>
                              openDRFinalizeDialog('review')
                            }
                          />
                        ) : (
                          <DRSettingsValueSkeleton />
                        )}
                      </StyledTableCell>
                    </StyledTableRow>
                  </StyledTableBody>
                </StyledTable>
              </StyledTableWrapper>
            </div>
          </TabsContent>

          <TabsContent value="personal" className="mt-0">
            <div className="flex flex-col gap-6">
              <div className="text-sm text-muted-foreground">
                Personal settings for wallet recovery will be available here.
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {isAdmin ? (
          <>
            <AdminChangeComparisonDialog
              open={isReviewAdminChangesOpen}
              onOpenChange={setIsReviewAdminChangesOpen}
            />
            {organization && isApiKeyModalOpen && (
              <ApiKeyModal
                open={isApiKeyModalOpen}
                onClose={() => {
                  setIsApiKeyModalOpen(false);
                  refreshApiKeyInfo();
                }}
                apiKeyInfo={apiKeyInfo}
              />
            )}
            {organization && isDefaultExpirationModalOpen && (
              <DefaultExpirationModal
                open={isDefaultExpirationModalOpen}
                onClose={() => setIsDefaultExpirationModalOpen(false)}
                onSaved={refetchOrganization}
                currentExpirationMinutes={organization.expirationMinutes}
              />
            )}
            {organization && isTransactionVisibilityModalOpen && (
              <TransactionVisibilityModal
                open={isTransactionVisibilityModalOpen}
                onClose={() => setIsTransactionVisibilityModalOpen(false)}
                onSaved={refetchOrganization}
                currentHideTransactionsFromNonPolicyParticipants={
                  organization.hideTransactionsFromNonPolicyParticipants
                }
              />
            )}

            {/* Education gate for admins reviewing an existing proposal */}
            <DRReviewEducationGateDialog
              open={isDRReviewGateOpen}
              onConfirm={() => {
                setIsDRReviewGateOpen(false);
                setIsDRSetupDialogOpen(true);
              }}
              onCancel={() => setIsDRReviewGateOpen(false)}
            />

            {/* Setup/review flow (mode is derived from active initiation proposal state) */}
            <DisasterRecoverySetupDialog
              open={isDRSetupDialogOpen}
              onClose={() => setIsDRSetupDialogOpen(false)}
              activeProposal={activeDRProposal}
              mode={drSetupDialogMode}
              externalSuccessSignal={disasterRecoverySetupSuccessSignal}
              onReviewProposal={() => {
                // Keep setup open in the background so review can stack on top.
                openInitiationProposalReview();
              }}
            />

            {/* Single DR proposal dialog instance keyed by the active variant. */}
            {activeDRProposalDialogVariant ? (
              <DRProposalReviewDialog
                open
                onClose={() => setActiveDRProposalDialogVariant(null)}
                onSignedSuccess={() => {
                  // Bubble initiation-review completion back to setup flow so it
                  // can present DRSuccessDialog while remaining open.
                  setDisasterRecoverySetupSuccessSignal((prev) => prev + 1);
                }}
                onReturnToSettings={() => {
                  setActiveDRProposalDialogVariant(null);
                  // After review/signing is completed, return fully to settings.
                  setIsDRSetupDialogOpen(false);
                }}
                variant={activeDRProposalDialogVariant}
                drConfig={activeDRDialogConfig}
                existingProposal={activeDRDialogProposal}
              />
            ) : null}
          </>
        ) : null}
      </PageContent>
    </div>
  );
}
